import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { AddModuleUsers } from 'app/library/module-user/actions/add-entities.action';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleUserState } from 'app/library/module-user/models/module-user.state';
import { Module } from 'app/library/module/models/module.model';
import { ModuleState } from 'app/library/module/models/module.state';
import { UserService } from 'app/library/user/services/user.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { UtilsState } from 'app/projects/subentity/src/lib/models/utils.state';
import { FetchUserInfo } from 'app/projects/user/src/lib/actions/fetch-user-info.action';
import { RefreshUsersOnlineState } from 'app/projects/user/src/lib/actions/refresh-users-online-state.action';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable, Subject, of } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { AllModulesStateModel } from '../../all-modules/models/all-modules.state-model';
import {
    AddMyModules,
    DeselectSelectedModules,
    FetchMyModule,
    FetchMyModules,
    FetchMyModulesFailure,
    FetchMyModulesSuccess,
    FetchParticipantModule,
    FetchParticipantModuleFailure,
    SearchMyModules,
    SelectFilteredModules,
    SetMyModules,
    SortMyModules,
    ToggleSelectedModule,
    UnsetMyModules,
} from '../actions';
import { FetchParticipantModuleSuccess } from '../actions/index';
import { MyModulesService } from '../services/my-modules.service';
import { MyModulesStateModel } from './my-modules.state-model';

@State<MyModulesStateModel>({
    name: 'myModules',
    defaults: new MyModulesStateModel(),
})
@Injectable()
export class MyModulesState extends SubentityState<ModuleUser> {
    private _gettingAll$ = new Subject<number>();

    @Selector()
    static getSearchTerm(state: MyModulesStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getSelectedEntityIds(state: MyModulesStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getTotalCount(state: AllModulesStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getSortBy(state: AllModulesStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: AllModulesStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getPageSize(state: AllModulesStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static isThereMorePages(state: MyModulesStateModel): boolean {
        if (state.totalCount === null) {
            return true;
        }

        return state.totalCount > (state.list || []).length;
    }

    @Selector([ModuleUserState.getEntitiesAsMap])
    static getFilteredEntities(state: MyModulesStateModel, moduleUsers: Map<string, ModuleUser>): ModuleUser[] {
        if (!moduleUsers) {
            return null;
        }

        return state.filtered.map((id) => moduleUsers.get(id));
    }

    @Selector([ModuleUserState.getEntitiesAsMap])
    static getEntities(state: MyModulesStateModel, moduleUsers: Map<string, ModuleUser>): ModuleUser[] {
        return UtilsState.getEntities(state, moduleUsers);
    }

    @Selector([MyModulesState.getEntities, ModuleState.getEntitiesAsMap])
    static getModules(state: MyModulesStateModel, myModuleUsers: ModuleUser[], modules: Map<string, Module>): Module[] {
        if (!modules || !myModuleUsers) {
            return [];
        }

        return myModuleUsers.map((moduleUser) => (modules.has(moduleUser.moduleId) ? modules.get(moduleUser.moduleId) : null)).filter((m) => !(m === null));
    }

    constructor(private _myModulesService: MyModulesService, private _userService: UserService, private _store: Store, private _featureService: FeatureService) {
        super(null, 0);

        this._propstoSearch = ['module', ...Module.props_to_search];
    }

    @Action(SetMyModules)
    setAll(ctx: StateContext<MyModulesStateModel>, action: SetMyModules): void {
        this._setSubentities(ctx, action, AddModuleUsers);
    }

    @Action(UnsetMyModules)
    unsetAll(ctx: StateContext<MyModulesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(AddModuleUsers)
    addModuleUsers({ getState, patchState }: StateContext<MyModulesStateModel>, { payload }: AddModuleUsers): void {
        const state = getState();

        const me = this._store.selectSnapshot(UserState.getMyInfo);
        const subentities = payload.filter((moduleUserExtended) => moduleUserExtended.userId === me.id);
        const payloadList = subentities.map((subentity) => subentity.id);

        // we merge the both old and new list
        // we remove the duplications with the Set class
        const newList: string[] = Array.from(new Set<string>([...(state.list || []), ...payloadList]));
        patchState({
            list: newList,
            filtered: [...newList], // filtered is same as list because of serverside pagination
            selected: [],
        });
    }

    @Action(AddMyModules)
    addToAll({ dispatch, getState, patchState }: StateContext<MyModulesStateModel>, { payload }: AddMyModules): void {
        const state = getState();

        const subentities = payload.map((subentity) => subentity.id);
        const newList = Array.from(new Set<string>([...(state.list || []), ...subentities]));

        dispatch(new AddModuleUsers(payload as ModuleUserExtended[]));
        patchState({
            list: newList,
            filtered: [...newList], // filtered is same as list because of serverside pagination
            selected: [],
        });
    }

    @Action(FetchMyModule)
    fetchSingle({ dispatch }: StateContext<MyModulesStateModel>, { payload }: FetchMyModule): Observable<ModuleUserExtended[]> {
        const me = this._store.selectSnapshot(UserState.getMyInfo);

        if (!me) {
            setTimeout(() => {
                this._store.dispatch(new FetchUserInfo());
            });
            return of([]);
        }

        // the request is returned so that dispatchers can await the fetched module user
        return this._myModulesService.getSingle(me.id, payload.moduleId, payload.includeAnalytics).pipe(
            tap((moduleUsers) => {
                // if does not exist, then logged in user is not on the module
                if (moduleUsers?.length) {
                    const tempModuleUser = moduleUsers[0];
                    const tempResponsibleUsers = tempModuleUser?.module.responsibleManagers;

                    const moduleUser: ModuleUserExtended = Object.assign(new ModuleUserExtended(), tempModuleUser, {
                        module: Object.assign(new Module(), tempModuleUser?.module, {
                            responsibleManagers: tempResponsibleUsers,
                        }),
                    });

                    dispatch(new AddMyModules([moduleUser]));

                    if (this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState)) {
                        dispatch(new RefreshUsersOnlineState([tempModuleUser.user]));
                    }
                } else {
                    dispatch(new AddMyModules([]));
                }
            })
        );
    }

    @Action(FetchParticipantModule)
    fetchParticipantModule({ dispatch }: StateContext<MyModulesStateModel>, { payload }: FetchParticipantModule): void {
        this._myModulesService.getSingle(payload.userId, payload.moduleId, payload.includeAnalytics).subscribe(
            (moduleUsers) => {
                dispatch([new AddMyModules(moduleUsers), new FetchParticipantModuleSuccess(moduleUsers)]);
            },
            (error: HttpErrorResponse) => {
                this._store.dispatch(new FetchParticipantModuleFailure());
            }
        );
    }

    @Action(FetchMyModules)
    fetchAll(ctx: StateContext<MyModulesStateModel>, { payload }: FetchMyModules): void {
        const me = this._store.selectSnapshot(UserState.getMyInfo);

        if (!me) {
            setTimeout(() => {
                this._store.dispatch([new FetchUserInfo(), new FetchMyModulesFailure()]);
            });
            return;
        }

        this._fetchSubentities(
            ctx,
            { payload: me.id },
            SetMyModules,
            FetchMyModulesSuccess,
            FetchMyModulesFailure,
            (payload
                ? this._myModulesService.getAll(me.id, '', payload.search, payload.sortBy, payload.sortOrder, payload.pageSize, payload.pageNumber, payload.tag)
                : this._myModulesService.getAll(me.id)
            ).pipe(
                map(({ data, pagination, user }) => {
                    ctx.patchState({ totalCount: pagination.number_of_results });

                    return data.map((query) => new ModuleUserExtended(query.module_user, query.final_grade, user));
                }),
                takeUntil(this._gettingAll$)
            )
        );
    }

    @Action(FetchMyModulesSuccess)
    fetchAllSuccess(ctx: StateContext<MyModulesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchMyModulesFailure)
    fetchAllFailure(ctx: StateContext<MyModulesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedModule)
    toggleSelect(ctx: StateContext<MyModulesStateModel>, action: ToggleSelectedModule): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredModules)
    selectAll(ctx: StateContext<MyModulesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedModules)
    deselectAll(ctx: StateContext<MyModulesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchMyModules)
    search(stateContext: StateContext<MyModulesStateModel>, { payload }: SearchMyModules): void {
        UtilsState.search(stateContext, payload, MyModulesStateModel.identifier);
    }

    @Action(SortMyModules)
    sort(stateContext: StateContext<MyModulesStateModel>, { payload }: SortMyModules): void {
        UtilsState.sort(stateContext, payload, MyModulesStateModel.identifier);
    }
}
